'use client';

/**
 * LoginPageHowItWorks — the callback flow + optional secure-mode branch.
 *
 * 3 sequential steps (open → auth → callback) describe what every customer
 * sees. A separate "Lock it down" block below presents the optional backend
 * token-validation path: without it the URL still works for anyone who has
 * it; with it, the page is gated end-to-end. Surfacing this as an option
 * (not step 4) makes the trade-off readable to non-technical buyers.
 */

import React from 'react';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import StepCards, { StepCardItem } from '../_shared/sections/StepCards';
import { useTranslation } from '@/i18n/client';
import { HiArrowRightOnRectangle, HiCursorArrowRays, HiKey, HiLockClosed } from 'react-icons/hi2';

type Props = { lang: Locale };

type StepKey = 'open' | 'auth' | 'callback';
type StepCopy = { num: string; title: string; description: string };

const STEP_NUMS: Record<StepKey, string> = {
  open: '01',
  auth: '02',
  callback: '03',
};

const STEP_ICONS: Record<StepKey, StepCardItem['Icon']> = {
  open: HiCursorArrowRays,
  auth: HiKey,
  callback: HiArrowRightOnRectangle,
};

const KEYS: StepKey[] = ['open', 'auth', 'callback'];

const LoginPageHowItWorks = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const copyFor = (k: StepKey): StepCopy => ({
    num: STEP_NUMS[k],
    title: t(`LoginPage/HowItWorks/${k}/title`),
    description: t(`LoginPage/HowItWorks/${k}/body`),
  });

  const steps: StepCardItem[] = KEYS.map((k) => {
    const c = copyFor(k);
    return {
      key: k,
      num: c.num,
      title: c.title,
      description: c.description,
      Icon: STEP_ICONS[k],
    };
  });

  return (
    <section
      id="how-it-works"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={t('LoginPage/HowItWorks/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('LoginPage/HowItWorks/title1')}{' '}
          <span style={{ color: '#B4C1D8' }}>{t('LoginPage/HowItWorks/title2')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('LoginPage/HowItWorks/subtitle')}
        </p>

        <StepCards steps={steps} accent="#A78BFA" minCardWidth={240} showArrow />

        <div
          className="rounded-2xl p-6 lg:p-7"
          style={{
            marginTop: 32,
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            gap: 18,
            alignItems: 'flex-start',
          }}
        >
          <div
            className="inline-flex items-center justify-center rounded-lg"
            style={{
              width: 40,
              height: 40,
              background: 'rgba(167,139,250,0.12)',
              border: '1px solid rgba(167,139,250,0.3)',
              flexShrink: 0,
            }}
          >
            <HiLockClosed className="w-5 h-5" style={{ color: '#A78BFA' }} />
          </div>
          <div>
            <div
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: 2,
                color: '#A78BFA',
                marginBottom: 6,
              }}
            >
              {t('LoginPage/HowItWorks/secure_mode/kicker')}
            </div>
            <h3
              className="font-semibold"
              style={{
                fontSize: 19,
                letterSpacing: '-0.01em',
                color: '#F5F7FA',
                margin: '0 0 8px',
              }}
            >
              {t('LoginPage/HowItWorks/secure_mode/title')}
            </h3>
            <p style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
              {t('LoginPage/HowItWorks/secure_mode/body')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LoginPageHowItWorks;
